import { api } from '../../../shared/lib/axios';
import { openAuthenticatedFile } from '../../../shared/lib/files';
import type { LocationType } from '../../../shared/lib/locations';

export type { LocationType };

export type AssetStatus = 'in_use' | 'available' | 'in_maintenance' | 'on_loan' | 'retired' | 'not_found';
export type AssetType = 'physical' | 'software';
export type AssetCategoryGroup = 'it_equipment' | 'mobile_device' | 'furniture' | 'software';
export type AssetDocumentType =
  | 'invoice'
  | 'responsibility_term'
  | 'warranty'
  | 'rental_contract'
  | 'technical_report'
  | 'maintenance_order'
  | 'other';

export interface AssetSummary {
  id: string;
  assetCode: string;
  displayCode: string | null;
  barcode: string | null;
  description: string;
  status: AssetStatus;
  assetType: AssetType;
  purchaseValue: string | null;
  warrantyExpiresAt: string | null;
  category: { id: string; name: string; group: AssetCategoryGroup } | null;
  currentCustodian: { id: string; name: string; type: string; jobTitle: string | null } | null;
  location: AssetLocation | null;
  costCenter: { id: string; code: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** Localização do ativo como o backend devolve (issue #300). */
export interface AssetLocation {
  id: string;
  code: string;
  name: string;
  type?: LocationType;
  /** Falso quando a localidade foi inativada depois da alocação (RN11). */
  active?: boolean;
  clientId?: string | null;
  clientName?: string | null;
}

export interface AssetDetail {
  asset: {
    id: string;
    assetCode: string;
    displayCode: string | null;
    serialNumber: string | null;
    barcode: string | null;
    description: string;
    brand: string | null;
    model: string | null;
    purchaseDate: string | null;
    purchaseValue: string | null;
    invoiceNumber: string | null;
    warrantyMonths: number | null;
    warrantyExpiresAt: string | null;
    status: AssetStatus;
    assetType: AssetType;
    specs: Record<string, unknown>;
    notes: string | null;
    companyId: string | null;
    createdAt: string;
    updatedAt: string;
  };
  category: { id: string; name: string; group: AssetCategoryGroup } | null;
  supplier: { id: string; corporateName: string; tradeName: string | null; documentNumber: string } | null;
  location: AssetLocation | null;
  detailedLocation: {
    building: string | null;
    floor: string | null;
    room: string | null;
    aisle: string | null;
    shelf: string | null;
    position: string | null;
  };
  costCenter: { id: string; code: string; name: string } | null;
  project: { id: string; code: string; name: string; status: string } | null;
  client: { id: string; name: string; document: string } | null;
  currentCustodian: {
    id: string;
    name: string;
    type: string;
    email: string | null;
    phone: string | null;
    jobTitle: string | null;
    assignedAt: string | null;
  } | null;
  company: { id: string; corporateName: string; tradeName: string | null } | null;
  recentMovements: Array<{
    id: string;
    type: string;
    description: string;
    performedByUserId: string;
    custodianId: string | null;
    locationId: string | null;
    locationName: string | null;
    previousLocation: { id: string; name: string } | null;
    newLocation: { id: string; name: string } | null;
    metadata: Record<string, unknown> | null;
    occurredAt: string;
  }>;
  documents: Array<{
    id: string;
    type: AssetDocumentType;
    fileName: string;
    fileSize: number;
    mimeType: string | null;
    createdAt: string;
  }>;
  alerts: Array<{
    id: string | null;
    type: string | null;
    severity: string;
    status: string | null;
    title: string;
    message: string;
    dueDate: string | null;
  }>;
}

export interface ListAssetsParams {
  status?: AssetStatus;
  assetType?: AssetType;
  categoryId?: string;
  custodianId?: string;
  locationId?: string;
  costCenterId?: string;
  projectId?: string;
  clientId?: string;
  companyId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface StockBalance {
  locationId: string;
  locationName: string;
  quantity: string;
}

export interface StockItem {
  id: string;
  code: string;
  description: string;
  category: string | null;
  unit: string;
  minimumStock: string;
  notes: string | null;
  active: boolean;
  balances: StockBalance[];
  totalQuantity: number;
  lowStock: boolean;
  createdAt: string;
  updatedAt: string;
}

export type StockMovementType = 'entry' | 'donation' | 'exit' | 'transfer' | 'adjustment' | 'return';

export interface StockMovement {
  id: string;
  type: StockMovementType;
  quantity: string;
  fromLocation: { id: string; name: string; fullAddress?: string | null } | null;
  toLocation: { id: string; name: string; fullAddress?: string | null } | null;
  reason: string | null;
  reference: string | null;
  purchaseOrder: { id: string; number: string } | null;
  purchaseQuotationItemId: string | null;
  purchaseOrderLine: { id: string; description: string; unit: string } | null;
  supplier: { id: string; name: string } | null;
  supplierInvoiceNumber: string | null;
  supplierInvoiceDate: string | null;
  unitCost: string | null;
  totalCost: string | null;
  invoiceDocument: { fileName: string; fileSize: number; mimeType: string | null } | null;
  performedBy: string | null;
  occurredAt: string;
}

export interface StockPurchaseOrder {
  id: string;
  number: string;
  supplier: string;
  status: string;
  amount: string;
  lines: Array<{
    id: string;
    description: string;
    unit: string;
    quantity: number;
    receivedQuantity: number;
    remainingQuantity: number;
    unitPrice: number;
  }>;
}

export type StockIssueRecipientType = 'custodian' | 'org_unit' | 'project';

export interface StockIssue {
  id: string;
  item: { id: string; code: string; description: string; unit: string } | null;
  location: { id: string; name: string } | null;
  recipientType: StockIssueRecipientType;
  recipient: { type: StockIssueRecipientType; id: string; name: string } | null;
  quantity: number;
  returnedQuantity: number;
  outstandingQuantity: number;
  isReturnable: boolean;
  reason: string | null;
  reference: string | null;
  issuedBy: string | null;
  issuedAt: string;
}

export interface CreateStockItemPayload {
  code: string;
  description: string;
  category?: string;
  unit: string;
  minimumStock?: number;
  notes?: string;
}

export type UpdateStockItemPayload = Partial<CreateStockItemPayload> & {
  active?: boolean;
};

export interface CreateStockMovementPayload {
  type: StockMovementType;
  quantity: number;
  fromLocationId?: string;
  toLocationId?: string;
  reason?: string;
  reference?: string;
  recipientType?: StockIssueRecipientType;
  custodianId?: string;
  orgUnitId?: string;
  projectId?: string;
  purchaseOrderId?: string;
  purchaseQuotationItemId?: string;
  supplierInvoiceNumber?: string;
  supplierInvoiceDate?: string;
  unitCost?: number;
  totalCost?: number;
}

export type UpdateStockLocationDetailsPayload = Partial<{
  unitName: string | null;
  building: string | null;
  floor: string | null;
  room: string | null;
  aisle: string | null;
  shelf: string | null;
  position: string | null;
}>;

export interface ReturnStockIssuePayload {
  quantity: number;
  reason?: string;
  reference?: string;
}

export type StockInventoryStatus = 'planned' | 'in_progress' | 'closed';

export interface StockInventorySummary {
  id: string;
  name: string;
  status: StockInventoryStatus;
  location: { id: string; name: string } | null;
  locationId: string;
  createdByUserId: string;
  startedByUserId: string | null;
  startedAt: string | null;
  closedByUserId: string | null;
  closedAt: string | null;
  createdAt: string;
  progress: { total: number; counted: number; divergent: number; pending: number };
}

export interface StockInventoryItem {
  id: string;
  inventoryId: string;
  stockItemId: string;
  item: {
    id: string;
    code: string;
    description: string;
    category: string | null;
    unit: string;
  } | null;
  expectedQuantity: number;
  countedQuantity: number | null;
  difference: number | null;
  notes: string | null;
  countedByUser: { id: string; name: string; email: string } | null;
  countedAt: string | null;
}

export interface StockInventoryDetail extends StockInventorySummary {
  createdByUser: { id: string; name: string; email: string } | null;
  startedByUser: { id: string; name: string; email: string } | null;
  closedByUser: { id: string; name: string; email: string } | null;
  items: StockInventoryItem[];
  adjustmentsApplied?: number;
}

export interface CreateStockInventoryPayload {
  name: string;
  locationId: string;
}

export interface CountStockInventoryItemPayload {
  countedQuantity: number;
  notes?: string;
}

export interface ListResponse<T> {
  data: T[];
  pagination: { limit: number; offset: number; count: number };
}

export interface CreateAssetPayload {
  assetCode: string;
  serialNumber?: string;
  barcode?: string;
  description: string;
  categoryId: string;
  brand?: string;
  model?: string;
  supplierId?: string;
  purchaseDate?: string;
  purchaseValue?: number;
  invoiceNumber?: string;
  costCenterId?: string;
  projectId?: string;
  clientId?: string;
  warrantyMonths?: number;
  warrantyExpiresAt?: string;
  locationId?: string;
  locationBuilding?: string;
  locationFloor?: string;
  locationRoom?: string;
  locationAisle?: string;
  locationShelf?: string;
  locationPosition?: string;
  notes?: string;
  specs?: Record<string, unknown>;
  companyId?: string;
}

export type UpdateAssetPayload = Partial<CreateAssetPayload>;

export const ATIVOS_KEYS = {
  all: ['ativos'] as const,
  list: (params?: ListAssetsParams) => ['ativos', 'list', params] as const,
  catalog: (params?: ListAssetsParams) => ['ativos', 'catalog', params] as const,
  detail: (id: string) => ['ativos', 'detail', id] as const,
  categories: ['ativos', 'categories'] as const,
  documents: (assetId: string) => ['ativos', assetId, 'documents'] as const,
  stockItems: (params?: { search?: string; active?: boolean; lowStock?: boolean; limit?: number; offset?: number }) =>
    ['ativos', 'stock-items', params] as const,
  stockMovements: (itemId: string) => ['ativos', 'stock-items', itemId, 'movements'] as const,
  stockIssues: (params?: object) => ['ativos', 'stock-issues', params] as const,
  stockInventories: (params?: { status?: StockInventoryStatus; locationId?: string; search?: string; limit?: number; offset?: number }) =>
    ['ativos', 'stock-inventories', params] as const,
  stockInventory: (id: string) => ['ativos', 'stock-inventory', id] as const,
  masterData: {
    locations: ['master-data', 'locations'] as const,
    costCenters: ['master-data', 'cost-centers'] as const,
    projects: ['master-data', 'projects'] as const,
    clients: ['master-data', 'clients'] as const,
  },
};

function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export async function listarAtivos(params?: ListAssetsParams): Promise<ListResponse<AssetSummary>> {
  const { data } = await api.get<ListResponse<AssetSummary>>(`/assets${buildQuery(params as Record<string, unknown>)}`);
  return data;
}

/**
 * Linha da grid "Listar Ativos" (issue #464): ativo ou licença.
 * `assetType=software` traz licenças e ativos de software.
 */
export type CatalogItem =
  | ({ kind: 'asset' } & AssetSummary)
  | ({ kind: 'license' } & LicenseSummary & { totalCost: string | null });

export async function listarCatalogo(params?: ListAssetsParams): Promise<ListResponse<CatalogItem>> {
  const { data } = await api.get<ListResponse<CatalogItem>>(`/assets/catalog${buildQuery(params as Record<string, unknown>)}`);
  return data;
}

export async function buscarAtivo(id: string): Promise<AssetDetail> {
  const { data } = await api.get<AssetDetail>(`/assets/${id}`);
  return data;
}

export async function criarAtivo(payload: CreateAssetPayload): Promise<AssetDetail> {
  const { data } = await api.post<AssetDetail>('/assets', payload);
  return data;
}

export async function atualizarAtivo(id: string, payload: UpdateAssetPayload): Promise<AssetDetail> {
  const { data } = await api.put<AssetDetail>(`/assets/${id}`, payload);
  return data;
}

export async function excluirAtivo(id: string): Promise<void> {
  await api.delete(`/assets/${id}`);
}

export interface CategoryGroup {
  group: AssetCategoryGroup;
  items: Array<{ id: string; name: string; group: AssetCategoryGroup }>;
}

export async function listarCategorias(): Promise<{ groups: CategoryGroup[] }> {
  const { data } = await api.get<{ groups: CategoryGroup[] }>('/asset-categories');
  return data;
}

export async function listarStockItems(params?: {
  search?: string;
  active?: boolean;
  lowStock?: boolean;
  limit?: number;
  offset?: number;
}): Promise<ListResponse<StockItem>> {
  const { data } = await api.get<ListResponse<StockItem>>(
    `/stock-items${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function criarStockItem(payload: CreateStockItemPayload): Promise<StockItem> {
  const { data } = await api.post<StockItem>('/stock-items', payload);
  return data;
}

export async function atualizarStockItem(id: string, payload: UpdateStockItemPayload): Promise<StockItem> {
  const { data } = await api.patch<StockItem>(`/stock-items/${id}`, payload);
  return data;
}

export async function listarStockPurchaseOrders(): Promise<{ data: StockPurchaseOrder[] }> {
  const { data } = await api.get<{ data: StockPurchaseOrder[] }>('/stock-items/purchase-orders');
  return data;
}

export async function listarStockMovements(itemId: string): Promise<ListResponse<StockMovement>> {
  const { data } = await api.get<ListResponse<StockMovement>>(`/stock-items/${itemId}/movements`);
  return data;
}

export async function criarStockMovement(
  itemId: string,
  payload: CreateStockMovementPayload,
  invoiceFile?: File,
): Promise<{ id: string; invoiceDocument: { fileName: string; fileSize: number; mimeType: string | null } | null }> {
  if (!invoiceFile) {
    const { data } = await api.post<{ id: string; invoiceDocument: { fileName: string; fileSize: number; mimeType: string | null } | null }>(
      `/stock-items/${itemId}/movements`,
      payload,
    );
    return data;
  }
  const formData = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (value !== undefined) formData.append(key, String(value));
  }
  formData.append('invoiceFile', invoiceFile);
  const { data } = await api.post<{ id: string; invoiceDocument: { fileName: string; fileSize: number; mimeType: string | null } | null }>(
    `/stock-items/${itemId}/movements`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function atualizarStockLocationDetails(
  locationId: string,
  payload: UpdateStockLocationDetailsPayload,
): Promise<MasterDataItem> {
  const { data } = await api.patch<MasterDataItem>(`/stock-items/locations/${locationId}/details`, payload);
  return data;
}

export async function uploadStockMovementInvoice(
  itemId: string,
  movementId: string,
  file: File,
): Promise<{ fileName: string; fileSize: number; mimeType: string | null }> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<{ fileName: string; fileSize: number; mimeType: string | null }>(
    `/stock-items/${itemId}/movements/${movementId}/invoice`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function downloadStockMovementInvoice(itemId: string, movementId: string): Promise<void> {
  await openAuthenticatedFile(`/api/stock-items/${itemId}/movements/${movementId}/invoice`);
}

export async function listarStockIssues(params?: {
  itemId?: string;
  locationId?: string;
  custodianId?: string;
  orgUnitId?: string;
  projectId?: string;
  outstandingOnly?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<ListResponse<StockIssue>> {
  const { data } = await api.get<ListResponse<StockIssue>>(
    `/stock-issues${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function devolverStockIssue(id: string, payload: ReturnStockIssuePayload): Promise<void> {
  await api.post(`/stock-issues/${id}/return`, payload);
}

export async function listarStockInventories(params?: {
  status?: StockInventoryStatus;
  locationId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<ListResponse<StockInventorySummary>> {
  const { data } = await api.get<ListResponse<StockInventorySummary>>(
    `/stock-inventories${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function buscarStockInventory(id: string): Promise<StockInventoryDetail> {
  const { data } = await api.get<StockInventoryDetail>(`/stock-inventories/${id}`);
  return data;
}

export async function criarStockInventory(payload: CreateStockInventoryPayload): Promise<StockInventoryDetail> {
  const { data } = await api.post<StockInventoryDetail>('/stock-inventories', payload);
  return data;
}

export async function iniciarStockInventory(id: string): Promise<StockInventoryDetail> {
  const { data } = await api.post<StockInventoryDetail>(`/stock-inventories/${id}/start`, {});
  return data;
}

export async function contarStockInventoryItem(
  inventoryId: string,
  itemId: string,
  payload: CountStockInventoryItemPayload,
): Promise<StockInventoryItem> {
  const { data } = await api.post<StockInventoryItem>(
    `/stock-inventories/${inventoryId}/items/${itemId}/count`,
    payload,
  );
  return data;
}

export async function fecharStockInventory(id: string): Promise<StockInventoryDetail> {
  const { data } = await api.post<StockInventoryDetail>(`/stock-inventories/${id}/close`, {});
  return data;
}

export interface AssetDocumentSummary {
  id: string;
  assetId: string;
  type: AssetDocumentType;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  uploadedByUserId: string | null;
  createdAt: string;
}

export async function listarDocumentos(assetId: string): Promise<{ data: AssetDocumentSummary[] }> {
  const { data } = await api.get<{ data: AssetDocumentSummary[] }>(`/assets/${assetId}/documents`);
  return data;
}

export async function uploadDocumento(
  assetId: string,
  file: File,
  type: AssetDocumentType,
): Promise<AssetDocumentSummary> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const { data } = await api.post<AssetDocumentSummary>(`/assets/${assetId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function baixarDocumento(assetId: string, documentId: string): Promise<void> {
  await openAuthenticatedFile(`/api/assets/${assetId}/documents/${documentId}/download`);
}

export async function excluirDocumento(assetId: string, documentId: string): Promise<void> {
  await api.delete(`/assets/${assetId}/documents/${documentId}`);
}

export interface MasterDataItem {
  id: string;
  code?: string;
  name: string;
  active?: boolean;
  type?: LocationType;
  clientId?: string | null;
  clientName?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  unitName?: string | null;
  building?: string | null;
  floor?: string | null;
  room?: string | null;
  aisle?: string | null;
  shelf?: string | null;
  position?: string | null;
  fullAddress?: string | null;
  document?: string;
  status?: string;
  client?: { id: string; name: string } | null;
}

async function listMasterData(path: string): Promise<ListResponse<MasterDataItem>> {
  const { data } = await api.get<ListResponse<MasterDataItem>>(path);
  return data;
}

// Localidades ativas: 500 cobre o catálogo inteiro nos selects de alocação
// (issue #300) sem paginar na tela.
export const listarLocations = () => listMasterData('/locations?limit=500');
export const listarCostCenters = () => listMasterData('/cost-centers');
export const listarProjects = () => listMasterData('/projects');
export const listarClients = () => listMasterData('/clients');

export async function listarFornecedores(): Promise<ListResponse<{ id: string; corporateName: string; tradeName: string | null; documentNumber: string }>> {
  const { data } = await api.get<ListResponse<{ id: string; corporateName: string; tradeName: string | null; documentNumber: string }>>(
    '/suppliers',
  );
  return data;
}

// ============================================================
// SPEC 02 · Responsabilidade, termos e assinatura
// ============================================================

/**
 * Type do custodiário — string livre porque abriga tanto perfis (`roles`)
 * quanto os slugs das entidades não pessoais (`department` e `client`).
 * Use `labelForCustodianType` do shared/utils/roleLabels para exibição.
 */
export type CustodianType = string;
export type SignatureStatus = 'pending' | 'signed' | 'not_required';

/**
 * Origem do vínculo do custodiário (#541):
 * - `user`: usuário ativo do Nexos;
 * - `department`: área/departamento (`org_units`);
 * - `client`: cliente.
 */
export type CustodianOrigin = 'user' | 'department' | 'client';

export interface CustodianTypeOption {
  slug: string;
  name: string;
  origin: CustodianOrigin;
}

export interface CustodianEntityOption {
  id: string;
  name: string;
  description: string | null;
}

export interface Custodian {
  id: string;
  type: CustodianType;
  name: string;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  userId: string | null;
  externalRef: string | null;
  /** Id da área/departamento quando `type='department'`. */
  departmentId: string | null;
  /** Id do cliente quando `type='client'`. */
  clientId: string | null;
  active: boolean;
  createdAt: string;
}

export interface AssetAssignment {
  id: string;
  assetId: string;
  custodianId: string;
  deliveredAt: string;
  responsibilityTermId: string | null;
  signatureStatus: SignatureStatus;
  signedAt: string | null;
  returnedAt: string | null;
  returnReason: string | null;
  termNumber: string | null;
  signedIp: string | null;
  signedByUserId: string | null;
  signedOnBehalfOf: string | null;
  signedDocumentHash: string | null;
  custodian?: Custodian;
  signedByUser?: { id: string; name: string; email: string };
  signedOnBehalfOfCustodian?: Custodian;
}

export interface CustodianContactFields {
  name?: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
  active?: boolean;
}

/**
 * O POST aceita exatamente uma origem: `userId`, `departmentId` ou
 * `clientId`. O backend deriva o `type` do custodiário a partir dela.
 */
export type CreateCustodianPayload =
  | ({ userId: string } & CustodianContactFields)
  | ({ departmentId: string } & CustodianContactFields)
  | ({ clientId: string } & CustodianContactFields);

export interface DeliverPayload {
  custodianId: string;
  deliveredAt: string;
  locationId?: string;
  notes?: string;
}

export interface ReturnPayload {
  returnedAt: string;
  returnReason: string;
  locationId?: string;
}

export interface TransferPayload {
  toCustodianId: string;
  occurredAt: string;
  /** Nova localização do ativo; sem ela o ativo fica onde está (RN07). */
  locationId?: string;
  reason?: string;
}

export async function listarCustodians(params: {
  type?: CustodianType; active?: boolean; search?: string; limit?: number; offset?: number;
} = {}): Promise<ListResponse<Custodian>> {
  const q = buildQuery({
    type: params.type,
    active: params.active === undefined ? undefined : String(params.active),
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  } as Record<string, unknown>);
  const { data } = await api.get<ListResponse<Custodian>>(`/custodians${q}`);
  return data;
}

export async function criarCustodian(payload: CreateCustodianPayload): Promise<Custodian> {
  const { data } = await api.post<Custodian>('/custodians', payload);
  return data;
}

/**
 * Catálogo do campo "Tipo" (#541): perfis ativos + Departamento + Cliente,
 * cada um com a origem que define o combo seguinte.
 */
export async function listarCustodianTypeOptions(): Promise<CustodianTypeOption[]> {
  const { data } = await api.get<{ data: CustodianTypeOption[] }>('/custodians/type-options');
  return data.data;
}

/** Opções ativas da entidade escolhida no campo "Tipo". */
export async function listarCustodianEntityOptions(
  type: 'department' | 'client',
): Promise<CustodianEntityOption[]> {
  const { data } = await api.get<{ data: CustodianEntityOption[] }>('/custodians/entity-options', {
    params: { type },
  });
  return data.data;
}

export async function entregarAtivo(assetId: string, payload: DeliverPayload): Promise<AssetAssignment> {
  const { data } = await api.post<AssetAssignment>(`/assets/${assetId}/assignments`, payload);
  return data;
}

export async function devolverAtivo(
  assetId: string,
  assignmentId: string,
  payload: ReturnPayload,
): Promise<AssetAssignment> {
  const { data } = await api.post<AssetAssignment>(
    `/assets/${assetId}/assignments/${assignmentId}/return`,
    payload,
  );
  return data;
}

export async function transferirAtivo(assetId: string, payload: TransferPayload): Promise<AssetAssignment> {
  const { data } = await api.post<AssetAssignment>(`/assets/${assetId}/transfer`, payload);
  return data;
}

export async function listarAssignments(assetId: string): Promise<{ data: AssetAssignment[] }> {
  const { data } = await api.get<{ data: AssetAssignment[] }>(`/assets/${assetId}/assignments`);
  return data;
}

export async function registrarAssinatura(assignmentId: string): Promise<AssetAssignment> {
  const { data } = await api.post<AssetAssignment>(`/assignments/${assignmentId}/sign`, {});
  return data;
}

export const ATIVOS_KEYS_S02 = {
  custodians: (params?: object) => ['custodians', params] as const,
  custodianTypeOptions: ['custodians', 'type-options'] as const,
  custodianEntityOptions: (type: 'department' | 'client') =>
    ['custodians', 'entity-options', type] as const,
  assignments: (assetId: string) => ['ativos', assetId, 'assignments'] as const,
};

// ============================================================
// SPEC 03 · Movimentações, Manutenção, Empréstimo, Substituição, Baixa
// ============================================================

export type MovementType =
  | 'acquisition'
  | 'delivery'
  | 'transfer'
  | 'replacement'
  | 'maintenance'
  | 'loan'
  | 'return'
  | 'retirement'
  | 'location_change';

export type RetirementType = 'disposal' | 'sale' | 'loss' | 'theft' | 'donation';

export interface AssetMovement {
  id: string;
  assetId: string;
  type: MovementType;
  description: string;
  performedByUserId: string;
  performedByUserName: string | null;
  performedByUserEmail: string | null;
  custodianId: string | null;
  custodianName: string | null;
  locationId: string | null;
  locationName: string | null;
  previousLocationId: string | null;
  previousLocationName: string | null;
  newLocationId: string | null;
  newLocationName: string | null;
  previousCustodianId: string | null;
  previousCustodianName: string | null;
  newCustodianId: string | null;
  newCustodianName: string | null;
  metadata: Record<string, unknown> | null;
  occurredAt: string;
  createdAt: string;
}

export interface MaintenanceCycle {
  id: string;
  assetId: string;
  startedAt: string;
  expectedReturnAt: string | null;
  maintenanceType: MaintenanceType;
  finishedAt: string | null;
  provider: string | null;
  cost: string | null;
  documentId: string | null;
  startDescription: string;
  finishDescription: string | null;
  startedByUserId: string;
  finishedByUserId: string | null;
  previousStatus: string;
  createdAt: string;
  updatedAt: string;
  document?: { id: string; fileName: string } | null;
  startedByUser?: { id: string; name: string; email: string };
  finishedByUser?: { id: string; name: string; email: string };
}

export type MaintenanceType = 'preventive' | 'corrective' | 'component_replacement' | 'furniture_refurbishment';

export interface ListMovementsParams {
  assetId?: string;
  type?: MovementType;
  performedByUserId?: string;
  /** Filtra por localidade de origem ou destino (CA08). */
  locationId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface StartMaintenancePayload {
  maintenanceType: MaintenanceType;
  description: string;
  provider?: string;
  expectedReturnAt?: string;
}
export interface FinishMaintenancePayload {
  description: string;
  cost?: number;
  documentId?: string;
}
export interface LoanPayload {
  custodianId: string;
  expectedReturnAt: string;
  deliveredAt?: string;
  locationId?: string;
  notes?: string;
}
export interface ReplacePayload {
  replacementAssetId: string;
  reason: string;
}
export interface RetirePayload {
  justification: string;
  approvedByUserId: string;
  retirementType: RetirementType;
  documentId?: string;
}

export async function listarMovementsByAsset(
  assetId: string,
  params: ListMovementsParams = {},
): Promise<ListResponse<AssetMovement>> {
  const { data } = await api.get<ListResponse<AssetMovement>>(
    `/assets/${assetId}/movements${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function listarMovementsGlobal(
  params: ListMovementsParams = {},
): Promise<ListResponse<AssetMovement>> {
  const { data } = await api.get<ListResponse<AssetMovement>>(
    `/movements${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

/**
 * Retorna Blob text/csv já com BOM UTF-8 + separador `;` (feito no backend).
 * Chamador dispara download com URL.createObjectURL.
 *
 * `truncated` sinaliza que o backend cortou o export no teto (10.000 linhas)
 * — o header X-Nexos-Csv-Truncated foi lido pra fora do envelope pra o
 * consumidor mostrar aviso.
 */
export async function exportMovementsCsv(
  params: ListMovementsParams = {},
): Promise<{ blob: Blob; truncated: boolean; rowCount: number | null }> {
  const response = await api.get(
    `/movements/export.csv${buildQuery(params as Record<string, unknown>)}`,
    { responseType: 'blob' },
  );
  const truncated = String(response.headers['x-nexos-csv-truncated'] ?? '') === 'true';
  const rowCountHeader = response.headers['x-nexos-csv-rows'];
  const rowCount = rowCountHeader ? Number(rowCountHeader) : null;
  return { blob: response.data as Blob, truncated, rowCount: Number.isFinite(rowCount) ? rowCount : null };
}

// ============================================================
// User lookup (SPEC 03 — dropdown do aprovador da baixa)
// ============================================================

export interface UserLookupItem {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  companyId?: string | null;
  active?: boolean;
}

export async function lookupUsers(params: {
  roles: string[];
  search?: string;
  limit?: number;
  includeInactive?: boolean;
}): Promise<{ data: UserLookupItem[] }> {
  const { data } = await api.get<{ data: UserLookupItem[] }>('/users/lookup', {
    params: {
      roles: params.roles.length > 0 ? params.roles.join(',') : undefined,
      search: params.search || undefined,
      limit: params.limit,
      includeInactive: params.includeInactive ? 'true' : undefined,
    },
  });
  return data;
}

export async function iniciarManutencao(
  assetId: string,
  payload: StartMaintenancePayload,
): Promise<MaintenanceCycle> {
  const { data } = await api.post<MaintenanceCycle>(
    `/assets/${assetId}/maintenance/start`,
    payload,
  );
  return data;
}

export async function finalizarManutencao(
  assetId: string,
  payload: FinishMaintenancePayload,
): Promise<MaintenanceCycle> {
  const { data } = await api.post<MaintenanceCycle>(
    `/assets/${assetId}/maintenance/finish`,
    payload,
  );
  return data;
}

export async function listarCiclosManutencao(
  assetId: string,
): Promise<{ data: MaintenanceCycle[] }> {
  const { data } = await api.get<{ data: MaintenanceCycle[] }>(
    `/assets/${assetId}/maintenance`,
  );
  return data;
}

export async function emprestarAtivo(
  assetId: string,
  payload: LoanPayload,
): Promise<AssetAssignment> {
  const { data } = await api.post<AssetAssignment>(`/assets/${assetId}/loan`, payload);
  return data;
}

export async function substituirAtivo(
  assetId: string,
  payload: ReplacePayload,
): Promise<{ replacement: AssetAssignment }> {
  const { data } = await api.post<{ replacement: AssetAssignment }>(
    `/assets/${assetId}/replace`,
    payload,
  );
  return data;
}

export async function baixarAtivo(
  assetId: string,
  payload: RetirePayload,
): Promise<{ id: string; status: 'retired' }> {
  const { data } = await api.post<{ id: string; status: 'retired' }>(
    `/assets/${assetId}/retire`,
    payload,
  );
  return data;
}

export const ATIVOS_KEYS_S03 = {
  movementsByAsset: (assetId: string, params?: object) =>
    ['ativos', assetId, 'movements', params] as const,
  movementsGlobal: (params?: object) => ['ativos', 'movements', 'global', params] as const,
  maintenanceCycles: (assetId: string) => ['ativos', assetId, 'maintenance'] as const,
};

// ============================================================
// SPEC 04 · Licenças, alertas
// ============================================================

export type LicenseType = 'microsoft' | 'adobe' | 'atlassian' | 'antivirus' | 'specialized' | 'saas';

export interface LicenseSummary {
  id: string;
  name: string;
  vendor: string;
  licenseType: LicenseType;
  totalSeats: number;
  usedSeats: number;
  availableSeats: number;
  contractedAt: string;
  expiresAt: string | null;
  ownerCustodian: { id: string; name: string } | null;
  supplier: { id: string; corporateName: string } | null;
  asset: { id: string; displayCode: string | null; assetCode: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface LicenseDetail extends LicenseSummary {
  costPerSeat: string | null;
  totalCost: string | null;
  notes: string | null;
}

export interface LicenseSeat {
  id: string;
  licenseId: string;
  custodianId: string;
  custodianName: string | null;
  assignedAt: string;
  revokedAt: string | null;
  assignedByUserId: string | null;
  revokedByUserId: string | null;
  createdAt: string;
}

export interface CreateLicensePayload {
  name: string;
  vendor: string;
  licenseType: LicenseType;
  totalSeats: number;
  contractedAt: string;
  expiresAt?: string;
  assetId?: string;
  ownerCustodianId?: string;
  supplierId?: string;
  costPerSeat?: number | null;
  notes?: string;
}
export type UpdateLicensePayload = Partial<CreateLicensePayload>;

export type AlertType =
  | 'warranty_expiring' | 'warranty_expired'
  | 'license_expiring' | 'license_expired' | 'license_limit_reached'
  | 'custodian_inactive' | 'asset_not_found' | 'inventory_pending'
  | 'loan_overdue' | 'stock_minimum_reached' | 'asset_damaged';
export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AlertStatus = 'open' | 'acknowledged' | 'resolved';

export interface AlertItem {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  status: AlertStatus;
  title: string;
  message: string;
  dueDate: string | null;
  assetId: string | null;
  assetCode: string | null;
  assetDisplayCode: string | null;
  licenseId: string | null;
  licenseName: string | null;
  custodianId: string | null;
  custodianName: string | null;
  assignmentId: string | null;
  stockItemId: string | null;
  stockItemCode: string | null;
  stockItemDescription: string | null;
  dedupeKey: string;
  acknowledgedByUserId: string | null;
  acknowledgedAt: string | null;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListAlertsParams {
  type?: AlertType;
  severity?: AlertSeverity;
  status?: AlertStatus;
  assetId?: string;
  licenseId?: string;
  stockItemId?: string;
  limit?: number;
  offset?: number;
}

export async function listarLicenses(params: {
  search?: string; vendor?: string; licenseType?: LicenseType; hasAvailability?: boolean;
  limit?: number; offset?: number;
} = {}): Promise<ListResponse<LicenseSummary>> {
  const { data } = await api.get<ListResponse<LicenseSummary>>(
    `/licenses${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function buscarLicense(id: string): Promise<{ license: LicenseDetail; seats: LicenseSeat[] }> {
  const { data } = await api.get<{ license: LicenseDetail; seats: LicenseSeat[] }>(`/licenses/${id}`);
  return data;
}

export async function criarLicense(payload: CreateLicensePayload): Promise<{ license: LicenseDetail; seats: LicenseSeat[] }> {
  const { data } = await api.post<{ license: LicenseDetail; seats: LicenseSeat[] }>('/licenses', payload);
  return data;
}

export async function atualizarLicense(id: string, payload: UpdateLicensePayload) {
  const { data } = await api.put<{ license: LicenseDetail; seats: LicenseSeat[] }>(`/licenses/${id}`, payload);
  return data;
}

export async function excluirLicense(id: string): Promise<void> {
  await api.delete(`/licenses/${id}`);
}

export async function atribuirSeat(licenseId: string, custodianId: string, assignedAt?: string): Promise<LicenseSeat> {
  const { data } = await api.post<LicenseSeat>(`/licenses/${licenseId}/seats`, { custodianId, assignedAt });
  return data;
}

export async function revogarSeat(licenseId: string, seatId: string): Promise<LicenseSeat> {
  const { data } = await api.post<LicenseSeat>(`/licenses/${licenseId}/seats/${seatId}/revoke`, {});
  return data;
}

export async function listarAlerts(params: ListAlertsParams = {}): Promise<ListResponse<AlertItem>> {
  const { data } = await api.get<ListResponse<AlertItem>>(
    `/alerts${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function contarAlertsOpen(): Promise<{ open: number; acknowledged: number }> {
  const { data } = await api.get<{ open: number; acknowledged: number }>('/alerts/count');
  return data;
}

export async function reconhecerAlert(id: string): Promise<AlertItem> {
  const { data } = await api.post<AlertItem>(`/alerts/${id}/acknowledge`, {});
  return data;
}

export async function resolverAlert(id: string): Promise<AlertItem> {
  const { data } = await api.post<AlertItem>(`/alerts/${id}/resolve`, {});
  return data;
}

export async function rodarMotorAlerts(): Promise<{ created: number; resolved: number; skipped: boolean }> {
  const { data } = await api.post<{ created: number; resolved: number; skipped: boolean }>('/alerts/run', {});
  return data;
}

export async function atualizarCustodianActive(id: string, active: boolean): Promise<Custodian> {
  const { data } = await api.patch<Custodian>(`/custodians/${id}`, { active });
  return data;
}

export interface UpdateCustodianPayload {
  active?: boolean;
  email?: string;
  phone?: string;
  jobTitle?: string;
}

export async function atualizarCustodian(id: string, payload: UpdateCustodianPayload): Promise<Custodian> {
  const { data } = await api.patch<Custodian>(`/custodians/${id}`, payload);
  return data;
}

export const ATIVOS_KEYS_S04 = {
  licenses: (params?: object) => ['ativos', 'licenses', params] as const,
  license: (id: string) => ['ativos', 'license', id] as const,
  alerts: (params?: object) => ['ativos', 'alerts', params] as const,
  alertsCount: ['ativos', 'alerts', 'count'] as const,
};

// ============================================================
// SPEC 05 · Inventário Patrimonial
// ============================================================

export type InventoryScopeType = 'area' | 'custodian' | 'location' | 'client' | 'project' | 'category' | 'all';
export type InventoryStatus = 'planned' | 'in_progress' | 'closed';
export type InventoryResult = 'pending' | 'found' | 'not_found' | 'divergent' | 'damaged';

export interface InventorySummary {
  id: string;
  name: string;
  scopeType: InventoryScopeType;
  scopeRefId: string | null;
  startedAt: string;
  deadline: string | null;
  status: InventoryStatus;
  closedAt: string | null;
  createdAt: string;
  progress: { total: number; found: number; not_found: number; divergent: number; damaged: number; pending: number };
}

export interface InventoryDetail {
  inventory: InventorySummary;
  createdByUser: { id: string; name: string; email: string } | null;
  closedByUser: { id: string; name: string; email: string } | null;
}

export interface InventoryItem {
  id: string;
  inventoryId: string;
  assetId: string;
  asset: {
    id: string;
    assetCode: string;
    displayCode: string | null;
    description: string;
    serialNumber: string | null;
    status: string;
  } | null;
  result: InventoryResult;
  expectedCustodian: { id: string; name: string } | null;
  expectedLocation: { id: string; name: string } | null;
  foundCustodian: { id: string; name: string } | null;
  foundLocation: { id: string; name: string } | null;
  divergenceNotes: string | null;
  checkedByUserId: string | null;
  checkedAt: string | null;
  updatedAt: string;
}

export interface CreateInventoryPayload {
  name: string;
  scopeType: InventoryScopeType;
  scopeRefId?: string;
  startedAt?: string;
  deadline?: string;
}

export interface CheckItemPayload {
  result: InventoryResult;
  foundCustodianId?: string;
  foundLocationId?: string;
  divergenceNotes?: string;
  damageNotes?: string;
}

export interface CheckByCodePayload {
  assetCode?: string;
  serialNumber?: string;
}

export async function listarInventarios(params: {
  status?: InventoryStatus; scopeType?: InventoryScopeType; search?: string; limit?: number; offset?: number;
} = {}): Promise<ListResponse<InventorySummary>> {
  const { data } = await api.get<ListResponse<InventorySummary>>(
    `/inventories${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function buscarInventario(id: string): Promise<InventoryDetail> {
  const { data } = await api.get<InventoryDetail>(`/inventories/${id}`);
  return data;
}

export async function criarInventario(payload: CreateInventoryPayload): Promise<InventoryDetail> {
  const { data } = await api.post<InventoryDetail>('/inventories', payload);
  return data;
}

export async function iniciarInventario(id: string) {
  const { data } = await api.post(`/inventories/${id}/start`, {});
  return data;
}

export async function fecharInventario(id: string): Promise<{
  id: string; status: 'closed'; closedAt: string;
  alertsCreated: number; notFoundCount: number; divergentCount: number; foundCount: number;
}> {
  const { data } = await api.post<any>(`/inventories/${id}/close`, { confirmClose: true });
  return data;
}

export async function listarItensInventario(
  id: string, params: { result?: InventoryResult; search?: string; limit?: number; offset?: number } = {},
): Promise<ListResponse<InventoryItem>> {
  const { data } = await api.get<ListResponse<InventoryItem>>(
    `/inventories/${id}/items${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function conferirItem(
  inventoryId: string, itemId: string, payload: CheckItemPayload,
): Promise<InventoryItem> {
  const { data } = await api.post<InventoryItem>(
    `/inventories/${inventoryId}/items/${itemId}/check`, payload,
  );
  return data;
}

export async function conferirPorCodigo(
  inventoryId: string, payload: CheckByCodePayload,
): Promise<InventoryItem> {
  const { data } = await api.post<InventoryItem>(
    `/inventories/${inventoryId}/items/check-by-code`, payload,
  );
  return data;
}

export async function previewInventario(
  scopeType: InventoryScopeType, scopeRefId?: string,
): Promise<{ count: number }> {
  const { data } = await api.get<{ count: number }>(
    `/inventories/preview${buildQuery({ scopeType, scopeRefId } as Record<string, unknown>)}`,
  );
  return data;
}

export async function relatorioInventario(id: string): Promise<{
  inventory: InventorySummary;
  totals: { total: number; found: number; not_found: number; divergent: number; damaged: number; pending: number };
  items: InventoryItem[];
}> {
  const { data } = await api.get<any>(`/inventories/${id}/report`);
  return data;
}

export async function exportarInventarioCsv(id: string): Promise<Blob> {
  const response = await api.get(`/inventories/${id}/report/export.csv`, { responseType: 'blob' });
  return response.data as Blob;
}

export async function inventariosDoAtivo(assetId: string): Promise<{ data: Array<{
  id: string; inventoryId: string; inventoryName: string | null; inventoryStatus: string | null;
  inventoryClosedAt: string | null; result: InventoryResult; divergenceNotes: string | null;
  checkedByUser: { id: string; name: string } | null; checkedAt: string | null;
}> }> {
  const { data } = await api.get<any>(`/assets/${assetId}/inventories`);
  return data;
}

export const ATIVOS_KEYS_S05 = {
  inventories: (params?: object) => ['ativos', 'inventories', params] as const,
  inventory: (id: string) => ['ativos', 'inventory', id] as const,
  items: (id: string, params?: object) => ['ativos', 'inventory', id, 'items', params] as const,
  report: (id: string) => ['ativos', 'inventory', id, 'report'] as const,
  preview: (scopeType: string, refId?: string) => ['ativos', 'inventory', 'preview', scopeType, refId] as const,
  byAsset: (assetId: string) => ['ativos', 'asset', assetId, 'inventories'] as const,
};

// ============================================================
//   SPEC 06 — Dashboard
// ============================================================

export interface DashboardParams {
  costCenterId?: string;
  locationId?: string;
}

export interface DashboardPayload {
  generatedAt: string;
  filters: { costCenterId: string | null; locationId: string | null };
  general: {
    total: number; inUse: number; available: number; inMaintenance: number;
    onLoan: number; retired: number; notFound: number;
  };
  financial: {
    totalValue: string;
    byCategory: Array<{ categoryId: string; category: string; value: string; count: number }>;
    byCostCenter: Array<{ costCenterId: string | null; costCenter: string; value: string; count: number }>;
  };
  operational: {
    byEmployee: Array<{ custodianId: string; custodian: string; count: number }>;
    byContractor: Array<{ custodianId: string; custodian: string; count: number }>;
    byProject: Array<{ projectId: string; project: string; count: number }>;
    byClient: Array<{ clientId: string; client: string; count: number }>;
  };
  risk: {
    warrantiesExpiring: number;
    licensesExpiring: number;
    assetsWithoutCustodian: number;
    assetsNotFound: number;
  };
}

export const ATIVOS_KEYS_S06 = {
  dashboard: (params?: DashboardParams) => ['ativos', 'dashboard', params ?? {}] as const,
};

export async function obterDashboardAtivos(params?: DashboardParams): Promise<DashboardPayload> {
  const { data } = await api.get<DashboardPayload>(
    `/assets/dashboard${buildQuery(params as Record<string, unknown>)}`,
  );
  return data;
}
